import { FormInstance } from "antd/lib/form";
import { AppRoles } from "../../authorization/Permissions";
import { message } from "antd";
import humanizeDuration from "humanize-duration";
import dayjs from "dayjs";
import { Dayjs } from "dayjs";
import { ReactNode } from "react";
import { MetadataDefinition } from "../../administration/types/Metadata";
import { TaskMetadata } from "../../tasks/types";

export class Utils {
    static readonly uploadAcceptExtensions =
        ".doc,.docx,.xls,.xlsx,.pdf,.xltm,.xlsm,.msg,.eml";

    static isUploadAccepted(fileName: string) {
        const dot = fileName.lastIndexOf(".");
        return (
            dot >= 0 &&
            Utils.uploadAcceptExtensions
                .split(",")
                .includes(fileName.slice(dot).toLowerCase())
        );
    }
    static htmlTags = /<(.|\n)*?>/g;
    private static symbols = /[()!.-1234567890:_=+@#$%^&*±[\]\n\t\\/]/gim;
    private static spaces = /\s{2,}/gim;

    static clearText(text: string | null) {
        if (!text) {
            return text;
        }

        return text.replace(this.symbols, "").replace(this.spaces, " ");
    }

    static downloadFile(url: string, mapForm: HTMLFormElement, formId: string) {
        mapForm.action = url;
        document.body.appendChild(mapForm);
        mapForm.submit();
        document.getElementById(formId)!.remove();
    }

    static readableFileSize(bytes: number, si: boolean) {
        const thresh = si ? 1000 : 1024;
        if (Math.abs(bytes) < thresh) {
            return bytes + " B";
        }
        const units = si
            ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
            : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
        let u = -1;
        do {
            bytes /= thresh;
            ++u;
        } while (Math.abs(bytes) >= thresh && u < units.length - 1);
        return bytes.toFixed(1) + " " + units[u];
    }

    static formatDateStringLong(date: string | number | null) {
        if (date) {
            let dateVal: Date = new Date(date);
            if (dateVal instanceof Date && Number.isNaN(dateVal.getTime())) {
                dateVal = new Date(Number.parseInt(date as string, 10));
            }
            return Intl.DateTimeFormat("en-Gb", {
                year: "numeric",
                month: "long",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            }).format(dateVal);
        } else {
            return "";
        }
    }

    static getDateTimestamp(
        date: string | number | null | undefined,
        ignoreCurrentTimezone: boolean = false,
    ) {
        if (date === null || date === undefined) {
            return null;
        }

        let dateVal: Date =
            typeof date === "string" && ignoreCurrentTimezone
                ? this.parseDateIgnoreTimeZone(date)
                : new Date(date);
        if (dateVal instanceof Date && Number.isNaN(dateVal.getTime())) {
            dateVal = new Date(Number.parseInt(date as string, 10));
        }
        const timestamp = dateVal.getTime();
        return Number.isNaN(timestamp) ? null : timestamp;
    }

    static formatDateLong(date: Date | null) {
        if (date) {
            return Intl.DateTimeFormat(this.getRegionLocales(), {
                year: "numeric",
                month: "long",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            }).format(date);
        } else {
            return "";
        }
    }

    static formatDateStringShort(
        date: string | null,
        excludeTime: boolean = false,
        excludeSeconds: boolean = false,
        excludeDate: boolean = false,
        ignoreCurrentTimezone: boolean = false,
    ) {
        if (date) {
            const parsedDate = ignoreCurrentTimezone
                ? this.parseDateIgnoreTimeZone(date)
                : new Date(date);
            return Intl.DateTimeFormat(this.getRegionLocales(), {
                year: excludeDate ? undefined : "numeric",
                month: excludeDate ? undefined : "2-digit",
                day: excludeDate ? undefined : "2-digit",
                hour: excludeTime ? undefined : "2-digit",
                minute: excludeTime ? undefined : "2-digit",
                second: excludeSeconds ? undefined : "2-digit",
            }).format(parsedDate);
        } else {
            return "";
        }
    }

    static dateStringToIso(date: string | null) {
        try {
            if (!date) {
                return "";
            }

            const dateString = new Date(date).toISOString();
            return dateString.includes("Invalid") ? date : dateString;
        } catch {
            return date;
        }
    }

    static formatDateAsUtcMidnightIso(date: Dayjs | string | null | undefined) {
        if (!date) {
            return null;
        }

        const parsedDate = dayjs.isDayjs(date) ? date : dayjs(date);
        if (!parsedDate.isValid()) {
            return null;
        }

        return new Date(
            Date.UTC(parsedDate.year(), parsedDate.month(), parsedDate.date()),
        ).toISOString();
    }

    static formatNumberWithSeparators(
        value: string | number | null | undefined,
    ) {
        if (value === undefined || value === null) {
            return value;
        }

        const normalizedValue =
            typeof value === "number" ? value.toString() : value;

        const parsedValue = Number(normalizedValue);
        if (Number.isNaN(parsedValue)) {
            return normalizedValue;
        }

        const fractionDigits = normalizedValue.split(".")[1]?.length ?? 0;
        return parsedValue.toLocaleString(undefined, {
            maximumFractionDigits: fractionDigits,
            minimumFractionDigits: fractionDigits,
        });
    }

    static getDuration(
        start: string | number,
        end: string | number | undefined | null,
    ) {
        if (!end) {
            return undefined;
        }

        const duration = new Date(end).getTime() - new Date(start).getTime();
        return humanizeDuration(duration);
    }

    static prepareRuntimeDataObjectFromForm(
        form: FormInstance,
        packageId: string | null = null,
    ) {
        const values = form!.getFieldsValue();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let obj: Record<string, { value: any }> = {};

        if (packageId) {
            obj = { PackageId: { value: packageId } };
        }

        for (const key of Object.getOwnPropertyNames(values)) {
            obj[key] = { value: form.getFieldValue(key) };
        }

        return obj;
    }

    static capitalizeWord(s: string) {
        return s.replace(/^\w/, (c) => c.toUpperCase());
    }

    static unCapitalizeWord(s: string) {
        return s.replace(/^\w/, (c) => c.toLowerCase());
    }

    static insertWhitespaceBeforeCapital(s: string) {
        s = s.replace(/[^0-9a-z]/gi, "");
        s = s.replace(/([a-z])([A-Z])/g, "$1 $2");
        s = s.replace(/([A-Z])([A-Z][a-z])/g, "$1 $2");
        return s;
    }

    static stringToHash(s: string) {
        let hash = 0;
        for (let i = 0; i < s.length; i++) {
            // tslint:disable-next-line:no-bitwise
            hash = s.charCodeAt(i) + ((hash << 5) - hash);
        }
        return hash;
    }

    static intToRGB(i: number) {
        // tslint:disable-next-line:no-bitwise
        const c = (i & 0x00ffffff).toString(16).toUpperCase();

        return "00000".substring(0, 6 - c.length) + c;
    }

    static handleDropdownItemSelection(
        e: React.MouseEvent<HTMLElement, MouseEvent>,
        sel: Selection,
    ) {
        const getTextNode = (node: ChildNode): ChildNode => {
            return node.lastChild === null ? node : getTextNode(node.lastChild);
        };
        const el = e.target as HTMLElement;
        if (
            el.className !== "ant-select-item-option-content" ||
            e.button !== 2
        ) {
            return;
        }
        el.focus();
        const range = document.createRange();
        const textNode = getTextNode(el);
        range.setStart(textNode!, 0);
        range.setEnd(textNode!, (textNode as Text).length);
        sel!.removeAllRanges();
        sel!.addRange(range);
    }

    static safeStringLocaleCompare = (
        a: string | undefined,
        b: string | undefined,
    ) => {
        if (!b && a) {
            return 1;
        } else if (b && !a) {
            return -1;
        } else if (!b && !a) {
            return 0;
        } else {
            return a!.localeCompare(b!);
        }
    };

    static priorityCompare = (a: string, b: string) => {
        if (a === "HIGH" && (b === "MEDIUM" || b === "LOW")) {
            return 1;
        } else if (a === "MEDIUM" && b === "LOW") {
            return 1;
        } else if (a === "MEDIUM" && b === "HIGH") {
            return -1;
        } else if (a === b) {
            return 0;
        }
        return -1;
    };

    static isExcelByExtension(name: string) {
        return (
            name.toLowerCase().endsWith("xlsx") ||
            name.toLowerCase().endsWith("xls") ||
            name.toLowerCase().endsWith("xltm") ||
            name.toLowerCase().endsWith("xlsm")
        );
    }

    static generateDocumentIconClass(documentName: string) {
        let colorClass: string = "default";

        if (!documentName) {
            return colorClass;
        }

        const docNameLower = documentName.toLowerCase();

        if (docNameLower.endsWith(".pdf")) {
            colorClass = "doc-pdf";
        }

        if (Utils.isExcelByExtension(docNameLower)) {
            colorClass = "doc-xls";
        }

        if (docNameLower.endsWith(".doc") || docNameLower.endsWith(".docx")) {
            colorClass = "doc-word";
        }

        if (docNameLower.endsWith(".xml")) {
            colorClass = "doc-xml";
        }

        return colorClass;
    }

    static generateDocumentIcon(documentName: string) {
        const colorClass = Utils.generateDocumentIconClass(documentName);
        return `alpha-icon sm ${colorClass}`;
    }

    static getQuilModules() {
        return {
            toolbar: [
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
            ],
        };
    }

    static getDateFormat(includeTime: boolean = false) {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const datePortion = timeZone.startsWith("America")
            ? "MM/DD/YYYY "
            : "DD/MM/YYYY";
        return includeTime ? `${datePortion} HH:mm:ss` : datePortion;
    }

    static formatDatePickerValue(
        dateVal: string | null,
        excludeTime: boolean = false,
        ignoreCurrentTimezone: boolean = false,
    ) {
        return dateVal
            ? dayjs(
                  Utils.formatDateStringShort(
                      dateVal,
                      excludeTime,
                      undefined,
                      undefined,
                      ignoreCurrentTimezone,
                  ),
                  Utils.getDateFormat(!excludeTime),
              )
            : undefined;
    }

    static escapeRegExp(str: string) {
        // eslint-disable-next-line no-useless-escape
        return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&");
    }

    static getAppRoleDisplayName(roleName: string) {
        switch (roleName) {
            case AppRoles.admin:
                return "System admin";
            case AppRoles.user:
                return "Project user";
            default:
                return roleName;
        }
    }

    static isValidUTCDateTime(str: string) {
        const date = new Date(str);
        return !isNaN(date.getTime()) && str.endsWith("Z") && str.includes("T");
    }

    static filterOption(
        input: string,
        option?:
            | {
                  label?: ReactNode;
                  value?: string;
                  customFilterValue?: string;
              }
            | undefined,
    ) {
        if (!option) {
            return false;
        }

        const fallbackLabel =
            typeof option.label === "string" || typeof option.label === "number"
                ? String(option.label)
                : "";
        const customFilterValue = option.customFilterValue ?? fallbackLabel;
        return customFilterValue.toLowerCase().includes(input.toLowerCase());
    }

    static trimTitleString(title: string) {
        let newTitle = title;
        if (title.length > 150) {
            newTitle = title.substring(0, 149) + "...";
        }
        return newTitle;
    }

    static copyTaskUrlToClipboard(taskId: string) {
        const url = `${window.location.origin}/tasks/${taskId}`;
        Utils.copyTextToClipboard(url);
    }

    static copyTextToClipboard(text: string) {
        const dummy = document.createElement("input");
        document.body.appendChild(dummy);
        dummy.value = text;
        dummy.select();
        dummy.setSelectionRange(0, 9999);
        document.execCommand("copy");
        document.body.removeChild(dummy);
        message.info("Copied to clipboard");
    }

    static getTaskPrioritiesForDropdown() {
        return [
            { value: "LOW", label: "Low" },
            { value: "MEDIUM", label: "Medium" },
            { value: "HIGH", label: "High" },
        ];
    }

    static getDuePeriodDays(period: "D" | "W" | "M" | "Y") {
        switch (period) {
            case "D":
                return 1;
            case "W":
                return 7;
            case "M":
                return 30;
            case "Y":
                return 365;
            default:
                return 1;
        }
    }

    static removeLocalTimeZone(date: Dayjs) {
        const year = date.toDate().getFullYear();
        const month = date.toDate().getMonth() + 1;
        const day = date.toDate().getDate();
        const parsedMonth = month < 10 ? `0${month}` : month;
        const parsedDay = day < 10 ? `0${day}` : day;

        return `${year}-${parsedMonth}-${parsedDay}T00:00:00.000Z`;
    }

    static ignoreLocalTimeZone(date: Dayjs) {
        const year = date.toDate().getFullYear();
        const month = date.toDate().getMonth() + 1;
        const day = date.toDate().getDate();
        const parsedMonth = month < 10 ? `0${month}` : month;
        const parsedDay = day < 10 ? `0${day}` : day;
        const hours = date.toDate().getHours();
        const parsedHours = hours < 10 ? `0${hours}` : hours;
        const minutes = date.toDate().getMinutes();
        const parsedMinutes = minutes < 10 ? `0${minutes}` : minutes;
        const seconds = date.toDate().getSeconds();
        const parsedSeconds = seconds < 10 ? `0${seconds}` : seconds;
        return `${year}-${parsedMonth}-${parsedDay}T${parsedHours}:${parsedMinutes}:${parsedSeconds}.000Z`;
    }

    static formatDuration(duration: string) {
        // Split into [hh, mm, ss.mmmmmm]
        const [hh, mm, ssFraction] = duration.split(":");
        const [ss, fraction = "0"] = ssFraction.split(".");

        const hours = parseInt(hh, 10);
        const minutes = parseInt(mm, 10);
        const seconds = parseInt(ss, 10);
        const milliseconds = parseInt(fraction.slice(0, 3).padEnd(3, "0"), 10); // take first 3 digits

        // Total seconds including fractions
        const totalSeconds =
            hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;

        if (totalSeconds < 1) {
            return "<1s";
        }

        const parts: string[] = [];

        if (hours > 0) {
            parts.push(`${hours}h`);
        }
        if (minutes > 0) {
            parts.push(`${minutes}m`);
        }
        const remainingSeconds = Math.floor(totalSeconds % 60);
        if (remainingSeconds > 0) {
            parts.push(`${remainingSeconds}s`);
        }

        return parts.join(" ");
    }

    static getMetadataParseValue = (
        nativeVal: unknown,
        definition: MetadataDefinition,
    ): unknown => {
        if (definition.isMultiValue) {
            const values = Array.isArray(nativeVal)
                ? nativeVal
                : nativeVal === null || nativeVal === undefined
                  ? []
                  : [nativeVal];
            const scalarDefinition = {
                ...definition,
                isMultiValue: false,
            };
            return values
                .map((value) =>
                    Utils.getMetadataParseValue(value, scalarDefinition),
                )
                .map((value) => String(value ?? ""))
                .join(", ");
        }

        const format = definition.format;
        switch (definition.fieldType) {
            case "Text":
                if (nativeVal && (definition.listValues?.length ?? 0) > 0) {
                    const selectedValue = definition.listValues?.find(
                        (item) => item.value === nativeVal,
                    );
                    if (selectedValue) {
                        nativeVal = selectedValue?.label;
                    }
                }

                if (format && nativeVal) {
                    const { prefix, suffix } = JSON.parse(format);
                    return `${prefix ?? ""}${nativeVal}${suffix ?? ""}`;
                }

                return nativeVal;

            case "Boolean":
                if (format && nativeVal !== undefined) {
                    const { trueVal, falseVal } = JSON.parse(format);
                    if (nativeVal && trueVal) {
                        return trueVal;
                    }
                    if (nativeVal === false && falseVal) {
                        return falseVal;
                    }
                }
                return nativeVal?.toString() ?? "";
            case "Number":
                if (format && nativeVal) {
                    const { precision, separator } = JSON.parse(format);
                    if (separator) {
                        if (precision !== undefined && precision !== null) {
                            return (nativeVal as number).toLocaleString(
                                undefined,
                                {
                                    maximumFractionDigits: precision,
                                    minimumFractionDigits: precision,
                                },
                            );
                        }

                        return Utils.formatNumberWithSeparators(
                            nativeVal as string | number | null | undefined,
                        );
                    } else if (precision) {
                        return (nativeVal as number).toFixed(precision);
                    } else {
                        return nativeVal;
                    }
                }
                return nativeVal;
            case "DateTime":
                switch (format) {
                    case ONLY_DATE:
                        return Utils.formatDateStringShort(
                            nativeVal as string | null,
                            true,
                            true,
                            false,
                            true,
                        );
                    case ONLY_TIME:
                        return Utils.formatDateStringShort(
                            nativeVal as string | null,
                            false,
                            false,
                            true,
                            true,
                        );
                    default:
                        return Utils.formatDateStringShort(
                            nativeVal as string | null,
                            false,
                            false,
                            false,
                            true,
                        );
                }
            default:
                return nativeVal;
        }
    };

    static groupBy = <T, K extends string | number | symbol>(
        arr: T[],
        key: (i: T) => K,
    ) =>
        arr.reduce(
            (groups, item) => {
                (groups[key(item)] ||= []).push(item);
                return groups;
            },
            {} as Record<K, T[]>,
        );

    private static getRegionLocales() {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return timeZone.startsWith("Europe")
            ? "en-Gb"
            : Intl.DateTimeFormat().resolvedOptions().locale;
    }

    private static parseDateIgnoreTimeZone(date: string) {
        const parsedDate = new Date(date);
        const userTimezoneOffset = parsedDate.getTimezoneOffset() * 60000;
        return new Date(parsedDate.getTime() + userTimezoneOffset);
    }

    static getMetadataValue(
        definition: MetadataDefinition | undefined,
        metadata: TaskMetadata | null | undefined,
        metadataDefinitions: MetadataDefinition[],
    ) {
        if (!definition) {
            return undefined;
        }
        if (definition.calculation && definition.calculation.enabled) {
            // Calculate value based on fieldA and fieldB metadata
            const metadataProject = metadata?.project;
            if (!metadataProject) {
                return null;
            }

            const fieldADef = metadataDefinitions.find(
                (d) => d.id === definition.calculation!.fieldA,
            );
            const fieldBDef = definition.calculation.fieldB
                ? metadataDefinitions.find(
                      (d) => d.id === definition.calculation!.fieldB,
                  )
                : null;

            const fieldAValue = fieldADef
                ? metadataProject[fieldADef.name]
                : null;
            const fieldBValue = fieldBDef
                ? metadataProject[fieldBDef.name]
                : null;

            // Convert to numbers
            const numA =
                fieldAValue !== null && fieldAValue !== undefined
                    ? Number(fieldAValue)
                    : null;
            const numB =
                fieldBValue !== null && fieldBValue !== undefined
                    ? Number(fieldBValue)
                    : null;
            const constant = definition.calculation.constant;

            // Calculate result
            let result: number | null = null;

            if (numA !== null && definition.calculation.operator) {
                // First operation: fieldA operator fieldB/constant
                const operand2 = numB !== null ? numB : 0;

                switch (definition.calculation.operator) {
                    case "+":
                        result = numA + operand2;
                        break;
                    case "-":
                        result = numA - operand2;
                        break;
                    case "*":
                        result = numA * operand2;
                        break;
                    case "/":
                        result = operand2 !== 0 ? numA / operand2 : null;
                        break;
                }

                // Second operation if operator2 exists and we have a constant
                if (
                    result !== null &&
                    definition.calculation.operator2 &&
                    constant !== null &&
                    numB !== null
                ) {
                    switch (definition.calculation.operator2) {
                        case "+":
                            result = result + constant;
                            break;
                        case "-":
                            result = result - constant;
                            break;
                        case "*":
                            result = result * constant;
                            break;
                        case "/":
                            result = constant !== 0 ? result / constant : null;
                            break;
                    }
                }
            }

            return result;
        }

        return metadata?.project?.[definition.name];
    }
}

export const EMAIL_REGEX_PATTERN =
    /[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/g;

export const safeStringLocaleCompare = (
    a: string | undefined,
    b: string | undefined,
) => {
    if (!b && a) {
        return 1;
    } else if (b && !a) {
        return -1;
    } else if (!b && !a) {
        return 0;
    } else {
        return b!.localeCompare(a!);
    }
};

export const FULL_DATE = "FULL_DATE";
export const ONLY_DATE = "ONLY_DATE";
export const ONLY_TIME = "ONLY_TIME";
